import { ArrowRight, ExternalLink } from "lucide-react";
import { InstagramIcon, TelegramIcon } from "@/features/contact/brand-icons";
import { Link } from "@/i18n/navigation";
import { cn } from "@/utils/cn";

const SITE_HOSTS = new Set(["exanabi.uz", "www.exanabi.uz"]);
const LOCALE_PREFIX = /^\/(uz|ru|en|tr)(?=\/|$)/;

/**
 * A link to a page of this site ("https://exanabi.uz/uz/tutor") as an in-app path without the locale
 * ("/tutor"), so it opens in the same tab and in the student's own language; null for any other site.
 */
function sitePath(url: string): string | null {
  try {
    const parsed = new URL(url);
    const ours = SITE_HOSTS.has(parsed.hostname) || (typeof window !== "undefined" && parsed.host === window.location.host);
    if (!ours) return null;
    return (parsed.pathname.replace(LOCALE_PREFIX, "") || "/") + parsed.search + parsed.hash;
  } catch {
    return null;
  }
}

function LinkIcon({ url }: { url: string }) {
  let host = "";
  try {
    host = new URL(url).hostname;
  } catch {
    // fall through to the generic icon
  }
  const props = { className: "h-4 w-4", "aria-hidden": true } as const;
  if (host.endsWith("instagram.com")) return <InstagramIcon {...props} />;
  if (host === "t.me" || host.endsWith("telegram.me")) return <TelegramIcon {...props} />;
  return <ExternalLink {...props} />;
}

const BUTTON = "inline-flex w-fit items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary-hover";

/**
 * An announcement's optional call-to-action button (admin-set link + label). A page of this site opens in the
 * same tab (e.g. "Friday bilan gaplashish" -> /tutor); another site (Instagram, Telegram) in a new tab so the
 * student keeps their place. `onNavigate` lets a dropdown close when its in-site link is followed.
 */
export function AnnouncementLink({ url, label, className, onNavigate }: { url: string; label: string; className?: string; onNavigate?: () => void }) {
  const path = sitePath(url);
  if (path) {
    return (
      <Link href={path} onClick={onNavigate} className={cn(BUTTON, className)}>
        {label}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={cn(BUTTON, className)}>
      <LinkIcon url={url} />
      {label}
    </a>
  );
}
